import { useState } from "react";
import { UserPlus, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { sanitizePhone, formatPhoneTR, isValidTurkishPhone } from "@/lib/validators";

/**
 * Compact "+" button that opens a tiny inline modal to create a customer
 * without leaving the current page (POS or Vault).
 *
 * Props:
 *   - onCreated(customer): callback with the freshly-created customer
 */
export default function QuickCustomerAdd({ onCreated, testid = "quick-customer-add" }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setPhone("");
    setSaving(false);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Ad soyad zorunlu");
      return;
    }
    if (phone && !isValidTurkishPhone(phone)) {
      toast.error("Telefon 10 haneli TR numarası olmalı (örn: 532 123 45 67)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/customers", {
        name: name.trim(),
        phone: sanitizePhone(phone),
        email: "",
        address: "",
      });
      toast.success(`${r.data.name} eklendi ✓`);
      setOpen(false);
      reset();
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Müşteri eklenemedi");
      setSaving(false);
    }
  };

  const phoneInvalid = phone.length > 0 && !isValidTurkishPhone(phone);
  const canSubmit = name.trim().length > 0 && !phoneInvalid;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 h-9 w-9 flex items-center justify-center border border-[--gold] text-[--gold] hover:bg-[--gold] hover:text-black transition-colors duration-200 active:scale-95"
        style={{ color: "var(--gold)", borderColor: "var(--gold)" }}
        title="Yeni müşteri ekle"
        aria-label="Yeni müşteri ekle"
        data-testid={testid}
      >
        <UserPlus size={14} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-modal`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm card-flat p-5 sm:p-6"
            style={{ background: "var(--surface)" }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="label-caps">Hızlı Ekle</div>
                <div className="font-display text-xl mt-1">Yeni Müşteri</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-neutral-400 hover:text-white"
                aria-label="Kapat"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-caps block mb-1.5">Ad Soyad</label>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
                  placeholder="Ali Yılmaz"
                  data-testid={`${testid}-name`}
                />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Telefon</label>
                <input
                  value={formatPhoneTR(phone)}
                  onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                  inputMode="tel"
                  maxLength={13}
                  className={`w-full bg-transparent border px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] ${
                    phoneInvalid ? "border-red-500/60" : "border-white/10"
                  }`}
                  placeholder="532 123 45 67"
                  data-testid={`${testid}-phone`}
                  onKeyDown={(e) => e.key === "Enter" && canSubmit && submit()}
                />
                {phoneInvalid && (
                  <div className="mt-1 text-[10px] text-red-400/90" data-testid={`${testid}-phone-error`}>
                    10 haneli TR numarası girin (örn: 532 123 45 67)
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={submit}
              disabled={saving || !canSubmit}
              className="btn-primary-gold w-full mt-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${testid}-submit`}
            >
              {saving ? "Ekleniyor…" : "Ekle ve Devam Et"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
